// ===============================================================
// File: src/components/Widgets/ReceiptsWidget.jsx
// Description: Dashboard widget showing synced receipts summary
// - Uses StorageContext virtual receipts loader
// - Falls back to LS if no folder/drive connected
// - Reactive via mudo:receipts-updated + drive polling
// ===============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../../lib/nav";
import WidgetHeader from "../Common/WidgetHeader";
import { useStorage } from "../../contexts/StorageContext";

const LS_KEY = "mudoReceipts";
const FS_PATH = "receipts/mudoReceipts.json";

function parseDateTS(d) {
  if (!d) return 0;
  const t = Date.parse(d);
  return Number.isFinite(t) ? t : 0;
}

function currencyFmt(currency = "AUD") {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return { format: (n) => `$${Number(n || 0).toFixed(2)}` };
  }
}

function deepEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function readLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const val = raw ? JSON.parse(raw) : [];
    return Array.isArray(val) ? val : [];
  } catch {
    return [];
  }
}

export default function ReceiptsWidget({ isDark = false, backTo = "/dashboard" }) {
  const navigate = useNavigate();
  const fmt = useMemo(() => currencyFmt("AUD"), []);
  const { hasWorkspace, isDriveLinked, loadJSON, loadJSONFresh } = useStorage();

  const [receipts, setReceipts] = useState([]);
  const ref = useRef(receipts);
  const drivePollJitterMsRef = useRef(Math.floor(Math.random() * 20000));
  useEffect(() => {
    ref.current = receipts;
  }, [receipts]);

  // --- Unified fetch (uses StorageContext virtual reader) ---
  const fetchReceipts = async (fresh = false) => {
    try {
      let data = [];
      if (hasWorkspace) {
        data = fresh && loadJSONFresh
          ? await loadJSONFresh(FS_PATH, [])
          : await loadJSON(FS_PATH, []);
      } else {
        data = readLS();
      }
      if (!Array.isArray(data)) data = [];
      if (!deepEqual(data, ref.current)) setReceipts(data);
    } catch {
      const ls = readLS();
      if (!deepEqual(ls, ref.current)) setReceipts(ls);
    }
  };

  // --- Initial load ---
  useEffect(() => {
    fetchReceipts();
  }, [hasWorkspace, loadJSON]);

  // --- React to global events ---
  useEffect(() => {
    const refresh = () => fetchReceipts();
    window.addEventListener("mudo:receipts-updated", refresh);
    window.addEventListener("mudo:vault-restored", refresh);
    window.addEventListener("mudo:files-restored", refresh);
    window.addEventListener("storage", (e) => {
      if (e.key === LS_KEY) refresh();
    });
    return () => {
      window.removeEventListener("mudo:receipts-updated", refresh);
      window.removeEventListener("mudo:vault-restored", refresh);
      window.removeEventListener("mudo:files-restored", refresh);
    };
  }, []);

  // --- Poll Drive every 15s ---
  useEffect(() => {
    let t;
    const BASE_POLL_MS = 15000;
    const pollMs = BASE_POLL_MS + drivePollJitterMsRef.current;
    const loop = async () => {
      try {
        if (isDriveLinked) await fetchReceipts(true);
      } finally {
        t = setTimeout(loop, pollMs);
      }
    };
    if (isDriveLinked) t = setTimeout(loop, pollMs);
    return () => t && clearTimeout(t);
  }, [isDriveLinked]);

  // --- Derive summary ---
  const stats = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    const rows = (receipts || []).map((r) => {
      const total = Number(r?.meta?.total ?? r?.parsed?.total ?? 0);
      const date = r?.meta?.date || r?.parsed?.date || "";
      const supplier = r?.parsed?.supplier || "";
      return {
        id: r.id || `rec-${Math.random()}`,
        name: r.name || "Receipt",
        supplier,
        total,
        ts: parseDateTS(date) || r.addedAt || 0,
      };
    });

    const recent = [...rows].sort((a, b) => b.ts - a.ts).slice(0, 4);
    const thisMonth = rows
      .filter((r) => {
        const d = new Date(r.ts);
        return d.getFullYear() === y && d.getMonth() === m;
      })
      .reduce((s, r) => s + (r.total || 0), 0);

    return { count: rows.length, recent, thisMonth };
  }, [receipts]);

  // --- UI helpers ---
  const card = isDark
    ? "rounded border p-3 bg-white/5 border-white/10"
    : "rounded border p-3 bg-white border-zinc-200";
  const label = "text-[11px] opacity-70";

  const openReceipts = () => navigateTo(navigate, ROUTES.RECEIPTS, { source: 'ReceiptsWidget:open' });

  return (
    <div className="space-y-3">
      <WidgetHeader
        title="Receipts"
        to={backTo}
        right={
          <div className="flex gap-2">
            <button
              onClick={openReceipts}
              className="px-3 py-1 text-sm rounded bg-amber-500 text-black hover:opacity-90"
            >
              Add
            </button>
            <button
              onClick={openReceipts}
              className="px-3 py-1 text-sm rounded text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              Open
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className={card}>
          <div className={label}>Total receipts</div>
          <div className="text-xl font-semibold tabular-nums">{stats.count}</div>
        </div>
        <div className={card}>
          <div className={label}>This month</div>
          <div className="text-xl font-semibold tabular-nums">
            {fmt.format(stats.thisMonth || 0)}
          </div>
        </div>
        <div className={card}>
          <div className={label}>Capture</div>
          <div className="text-xs">Scan & OCR</div>
        </div>
      </div>

      <div className={card}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium text-sm">Recent</div>
          <button
            onClick={openReceipts}
            className="text-xs underline opacity-70 hover:opacity-100"
          >
            manage
          </button>
        </div>
        {stats.recent.length === 0 ? (
          <div className="text-xs opacity-60">No receipts yet.</div>
        ) : (
          <ul className="space-y-1 text-xs">
            {stats.recent.map((r) => (
              <li key={r.id} className="flex justify-between gap-2">
                <span className="truncate">
                  {r.name} {r.supplier ? <span className="opacity-60">({r.supplier})</span> : null}
                </span>
                <span className="shrink-0 tabular-nums">
                  {r.total ? fmt.format(r.total) : "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
